import React, { useCallback, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import type { RecordSourceSelectorProxy } from 'relay-runtime';
import { IssueRealtimeUpdates } from '@atlassian/jira-issue-realtime-updates/src/ui/IssueRealtimeUpdates.tsx';
import { useIssueRealtimeUpdater } from '../../controllers/issue-realtime-updater/index.tsx';
import type { IssueRealtimeUpdater$key } from './__generated__/IssueRealtimeUpdater.graphql';
import type { IssueRealtimeUpdater_project$key } from './__generated__/IssueRealtimeUpdater_project.graphql';
import type { IssueRealtimeUpdater_spreadsheetGroupConnection$key } from './__generated__/IssueRealtimeUpdater_spreadsheetGroupConnection.graphql';
import { useRealtimeUpdateOnRefresh, useRealtimeUpdatesForIssues } from './utils.tsx';

type Props = {
	project: IssueRealtimeUpdater_project$key;
	issues?: IssueRealtimeUpdater$key | null;
	groups?: IssueRealtimeUpdater_spreadsheetGroupConnection$key | null;
	isHierarchyEnabled?: boolean;
	isStatusTrackingEnabled?: boolean;
	onRefetch?: () => void;
};

export const IssueRealtimeUpdater = ({
	project,
	issues,
	groups,
	isHierarchyEnabled,
	isStatusTrackingEnabled,
	onRefetch: onRefetchProp,
}: Props) => {
	const realtimeUpdatesForIssues = useRealtimeUpdatesForIssues();
	const onRefetch = useRealtimeUpdateOnRefresh(onRefetchProp);

	const issueConnection = useFragment(
		graphql`
			fragment IssueRealtimeUpdater on JiraIssueConnection {
				__id
				jql
				edges {
					__typename
				}
			}
		`,
		issues ?? null,
	);

	const groupConnection = useFragment(
		graphql`
			fragment IssueRealtimeUpdater_spreadsheetGroupConnection on JiraSpreadsheetGroupConnection {
				__id
				jql
				groupByField
				edges {
					__typename
				}
			}
		`,
		groups ?? null,
	);

	const { projectId, key: projectKey } = useFragment(
		graphql`
			fragment IssueRealtimeUpdater_project on JiraProject {
				projectId @required(action: THROW)
				key
			}
		`,
		project,
	);

	const { issueDeleteUpdater } = useIssueRealtimeUpdater();

	const onDelete = useCallback(
		(store: RecordSourceSelectorProxy, issueAri: string) => {
			return issueDeleteUpdater({
				store,
				issueResourceId: issueAri,
				groupConnectionId: groupConnection?.__id,
				projectKey,
			});
		},
		[groupConnection?.__id, projectKey, issueDeleteUpdater],
	);

	const onIssuesCreatedUpdated = useCallback(
		(issuesCreatedUpdated: { issueAri: string; issueId: string }[]) => {
			realtimeUpdatesForIssues({
				issues: issuesCreatedUpdated,
				isHierarchyEnabled: isHierarchyEnabled ?? false,
				jql: issueConnection?.jql || groupConnection?.jql || '',
				groupByFieldId: groupConnection?.groupByField || undefined,
				groupConnectionId: groupConnection?.__id,
				numGroupsLoaded: groupConnection?.edges?.length ?? 0,
				projectKey,
				isStatusTrackingEnabled: isStatusTrackingEnabled ?? false,
			});
		},
		[
			realtimeUpdatesForIssues,
			isHierarchyEnabled,
			issueConnection?.jql,
			groupConnection?.jql,
			groupConnection?.groupByField,
			groupConnection?.__id,
			groupConnection?.edges?.length,
			projectKey,
			isStatusTrackingEnabled,
		],
	);

	const projectIds = useMemo(() => [projectId], [projectId]);

	return (
		<IssueRealtimeUpdates
			projectIds={projectIds}
			onIssuesCreatedUpdated={onIssuesCreatedUpdated}
			onDelete={onDelete}
			onRefetch={onRefetch}
		/>
	);
};
